import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DoDisturbOnRoundedIcon from "@mui/icons-material/DoDisturbOnRounded";
import { collection, onSnapshot } from "firebase/firestore";
import { auth, db } from "../../../firebase";
import {
  acceptConnectionRequest,
  cancelConnectionRequest,
  declineConnectionRequest,
  fetchIncomingConnectionRequests,
  fetchOutgoingConnectionRequests,
} from "../../../services/profileService";
import {
  getProfilePathByUid,
  getPublicHandle,
  getPublicProfilePathByHandle,
  normalizePublicHandle,
} from "../../../utils/publicProfile";
import { ellipsizeProfileText } from "../../../utils/profileText";
import { APP_SHELL_LAYOUT } from "../../../constants/constants";

// The bottom nav is fixed and sits on top of the page, so a long request list ends up
// running underneath it. Clear the nav's full height plus the device's bottom safe area,
// keeping the page's own 24px of breathing room.
const PAGE_BOTTOM_PADDING_CSS = `calc(${APP_SHELL_LAYOUT.BOTTOM_NAV_OFFSET_CSS} + 24px)`;

const PAGE_HEADER_SX = {
  fontWeight: 400,
  letterSpacing: "-0.01em",
  lineHeight: 1.15,
};

function ConnectionRequestSection({
  title,
  empty,
  rows,
  primaryActionLabel,
  primaryActionIcon,
  secondaryActionLabel,
  secondaryActionIcon,
  secondaryActionCompact = false,
  onPrimaryAction,
  onSecondaryAction,
}) {
  return (
    <Box sx={{ mt: 2.5 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.75 }}>
        {title}
      </Typography>

      {rows.length === 0 ? (
        <Alert severity="info">{empty}</Alert>
      ) : (
        <List sx={{ p: 0 }}>
          {rows.map((row) => {
            const handle =
              normalizePublicHandle(row.publicHandle || "") ||
              getPublicHandle(row.userName);
            const displayName = ellipsizeProfileText(row.displayName || "Unnamed Profile");
            const secondaryText = handle
              ? `@${ellipsizeProfileText(handle)}`
              : ellipsizeProfileText(row.email || "No public handle");
            const profilePath = getPublicProfilePathByHandle(handle) || getProfilePathByUid(row.uid);

            return (
              <ListItem key={row.uid} disablePadding sx={{ mb: 0.9 }}>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    minWidth: 0,
                    gap: 0.75,
                    p: 0.625,
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "divider",
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.92)",
                  }}
                >
                  <ListItemButton
                    component={RouterLink}
                    to={profilePath}
                    sx={{
                      minWidth: 0,
                      flex: 1,
                      py: 0.375,
                      px: 0.5,
                      borderRadius: 1,
                    }}
                    >
                      <Avatar
                        src={row.profileImage || undefined}
                        sx={{ mr: 1, width: 40, height: 40 }}
                      >
                      {(displayName || handle || "P").charAt(0).toUpperCase()}
                    </Avatar>
                    <ListItemText
                      primary={displayName}
                      secondary={secondaryText}
                      sx={{ minWidth: 0 }}
                      primaryTypographyProps={{
                        fontWeight: 700,
                        sx: {
                          whiteSpace: "normal",
                          overflow: "visible",
                          textOverflow: "clip",
                          wordBreak: "break-word",
                        },
                      }}
                      secondaryTypographyProps={{
                        sx: {
                          color: "text.secondary",
                          whiteSpace: "normal",
                          overflow: "visible",
                          textOverflow: "clip",
                          wordBreak: "break-word",
                        },
                      }}
                    />
                  </ListItemButton>

                  <Stack direction="row" spacing={0.75} sx={{ flexShrink: 0 }}>
                    {secondaryActionLabel ? (
                      secondaryActionCompact ? (
                        <IconButton
                          aria-label={secondaryActionLabel}
                          size="small"
                          color="default"
                          onClick={() => onSecondaryAction(row)}
                          sx={{
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: "50%",
                            width: 36,
                            height: 36,
                          }}
                        >
                          {secondaryActionIcon}
                        </IconButton>
                      ) : (
                        <Button
                          size="small"
                          color="inherit"
                          variant="outlined"
                          startIcon={secondaryActionIcon}
                          sx={{ whiteSpace: "nowrap" }}
                          onClick={() => onSecondaryAction(row)}
                        >
                          {secondaryActionLabel}
                        </Button>
                      )
                    ) : null}
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={primaryActionIcon}
                      onClick={() => onPrimaryAction(row)}
                      sx={{ minHeight: 36, whiteSpace: "nowrap", pl: 1.2, pr: 2 }}
                    >
                      {primaryActionLabel}
                    </Button>
                  </Stack>
                </Box>
              </ListItem>
            );
          })}
        </List>
      )}
    </Box>
  );
}

export default function ConnectionRequestsPage() {
  const currentUser = auth.currentUser;
  const [loading, setLoading] = useState(true);
  const [incomingRequests, setIncomingRequests] = useState([]);
  const [outgoingRequests, setOutgoingRequests] = useState([]);
  const [snackbar, setSnackbar] = useState("");

  const countsLabel = useMemo(
    () =>
      `${incomingRequests.length} incoming, ${outgoingRequests.length} outgoing`,
    [incomingRequests.length, outgoingRequests.length]
  );

  useEffect(() => {
    if (!currentUser?.uid) {
      setIncomingRequests([]);
      setOutgoingRequests([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);

    const incomingRef = collection(db, "users", currentUser.uid, "connectionRequests");
    const outgoingRef = collection(db, "users", currentUser.uid, "sentConnectionRequests");

    const loadAll = async () => {
      try {
        const [incoming, outgoing] = await Promise.all([
          fetchIncomingConnectionRequests(currentUser.uid),
          fetchOutgoingConnectionRequests(currentUser.uid),
        ]);

        setIncomingRequests(incoming);
        setOutgoingRequests(outgoing);
      } catch (err) {
        console.error("Failed to load connection requests:", err);
        setIncomingRequests([]);
        setOutgoingRequests([]);
        setSnackbar("Failed to load connection requests.");
      } finally {
        setLoading(false);
      }
    };

    const unsubIncoming = onSnapshot(
      incomingRef,
      () => {
        loadAll();
      },
      (err) => {
        console.error("Failed to subscribe to incoming connection requests:", err);
        setLoading(false);
      }
    );

    const unsubOutgoing = onSnapshot(
      outgoingRef,
      () => {
        loadAll();
      },
      (err) => {
        console.error("Failed to subscribe to outgoing connection requests:", err);
        setLoading(false);
      }
    );

    return () => {
      unsubIncoming();
      unsubOutgoing();
    };
  }, [currentUser]);

  const handleAccept = async (row) => {
    if (!currentUser?.uid || !row?.uid) return;

    try {
      await acceptConnectionRequest(currentUser.uid, row.uid);
      setIncomingRequests((prev) => prev.filter((request) => request.uid !== row.uid));
      setOutgoingRequests((prev) => prev.filter((request) => request.uid !== row.uid));
      setSnackbar("Connection request accepted.");
    } catch (err) {
      console.error("Failed to accept connection request:", err);
      setSnackbar("Failed to accept connection request.");
    }
  };

  const handleDecline = async (row) => {
    if (!currentUser?.uid || !row?.uid) return;

    try {
      await declineConnectionRequest(currentUser.uid, row.uid);
      setIncomingRequests((prev) => prev.filter((request) => request.uid !== row.uid));
      setSnackbar("Connection request declined.");
    } catch (err) {
      console.error("Failed to decline connection request:", err);
      setSnackbar("Failed to decline connection request.");
    }
  };

  const handleCancel = async (row) => {
    if (!currentUser?.uid || !row?.uid) return;

    try {
      await cancelConnectionRequest(currentUser.uid, row.uid);
      setOutgoingRequests((prev) => prev.filter((request) => request.uid !== row.uid));
      setSnackbar("Connection request canceled.");
    } catch (err) {
      console.error("Failed to cancel connection request:", err);
      setSnackbar("Failed to cancel connection request.");
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: "40vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 3, pb: PAGE_BOTTOM_PADDING_CSS }}>
      <Typography variant="h4" sx={{ ...PAGE_HEADER_SX, mb: 0.5 }}>
        Friend Requests
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        Review who wants to become friends and manage requests you have already sent.
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
        {countsLabel}
      </Typography>

      <ConnectionRequestSection
        title="Incoming"
        empty="No incoming connection requests."
        rows={incomingRequests}
        primaryActionLabel="Accept"
        primaryActionIcon={<CheckRoundedIcon fontSize="small" />}
        secondaryActionLabel="Decline"
        secondaryActionIcon={<CloseRoundedIcon fontSize="small" />}
        secondaryActionCompact
        onPrimaryAction={handleAccept}
        onSecondaryAction={handleDecline}
      />

      <ConnectionRequestSection
        title="Sent"
        empty="No pending outgoing connection requests."
        rows={outgoingRequests}
        primaryActionLabel="Cancel"
        primaryActionIcon={<DoDisturbOnRoundedIcon fontSize="small" />}
        secondaryActionLabel=""
        secondaryActionIcon={null}
        onPrimaryAction={handleCancel}
        onSecondaryAction={() => {}}
      />

      <Snackbar open={Boolean(snackbar)} autoHideDuration={3000} onClose={() => setSnackbar("")}>
        <Alert onClose={() => setSnackbar("")} variant="filled" severity="info" sx={{ width: "100%" }}>
          {snackbar}
        </Alert>
      </Snackbar>
    </Container>
  );
}
